import { TaskDueDateState } from "@jugl-web/domain-resources/tasks";
import { TaskDueDateFormPopover } from "@jugl-web/domain-resources/tasks/components/DueDatePickerPopover";
import { isTicketOverdue } from "@jugl-web/domain-resources/tickets/utils/isTicketOverdue";
import { UpdateTicketPayload } from "@jugl-web/rest-api";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { TableCell } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { TicketPropertyPill } from "@jugl-web/domain-resources/tickets/components/TicketPropertyPill";
import { FC, useCallback, useMemo } from "react";
import { ReactComponent as CalendarDueIcon } from "./assets/calendar-due.svg";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { ReactComponent as CalendarBlankIcon } from "./assets/calendar-blank.svg";
import { AppearingPlaceholderText } from "../AppearingPlaceholderText";

interface DueDateCellContentProps {
  ticket: TicketListItemModel;
  onUpdate: (data: UpdateTicketPayload) => void;
}

export const DueDateCellContent: FC<DueDateCellContentProps> = ({
  ticket,
  onUpdate,
}) => {
  const { formatDateTimeLabel } = useFormattedDate();

  const dueDate = useMemo(
    () => (ticket.due_at ? new Date(ticket.due_at) : null),
    [ticket.due_at]
  );

  const isOverdue = isTicketOverdue({ dueDate, status: ticket.status });

  const handleSubmit = useCallback(
    (state: TaskDueDateState | null) => {
      const newDueDate = state ? state.date : null;

      if (JSON.stringify(newDueDate) === JSON.stringify(dueDate)) {
        return;
      }

      onUpdate({
        due_at: newDueDate ? newDueDate.toISOString() : null,
      });
    },
    [dueDate, onUpdate]
  );

  const Icon = isOverdue ? CalendarDueIcon : CalendarIcon;

  return (
    <TaskDueDateFormPopover
      placement="bottom"
      className="w-[375px]"
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          as={TableCell}
          ref={triggerRef}
          isFocused={isOpen}
          isHoverable
          classNames={{
            content: "flex min-w-0 items-center gap-2 overflow-hidden",
          }}
        >
          {dueDate ? (
            <TicketPropertyPill
              icon={<Icon className="shrink-0" />}
              label={formatDateTimeLabel(dueDate, "concise")}
              className={cx(isOverdue && "border-tertiary-300")}
            />
          ) : (
            <AppearingPlaceholderText
              icon={<CalendarBlankIcon />}
              label="Set due date"
              isStatic={isOpen}
            />
          )}
        </Trigger>
      )}
      initialState={dueDate ? { date: dueDate } : null}
      onSubmit={handleSubmit}
    />
  );
};
